// LearnCore Frontend - API客户端

import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { appConfig, logEnvironmentInfo } from '../config/env';
import type {
  User,
  StudySet,
  PublicStudySet,
  StudySetDetails,
  CreateStudySetRequest,
  UpdateStudySetRequest,
  RegisterRequest,
  LoginRequest,
  AuthResponse,
  ApiResponse,
  UserSettings,
  StudySessionData,
  SaveStudySessionRequest,
  StudyMode,
  AdaptiveSessionState,
  AdaptiveStartResponseFlashcard,
  AdaptiveAnswerResponseFlashcard,
  StartStudyTimeRequest,
  UpdateStudyTimeRequest,
  EndStudyTimeRequest,
  StudyTimeStats,
  StudyTimeLog,
  Tag,
  CreateCardRequest,
  UpdateCardRequest,
  CreateCardsBatchRequest,
  ReadingPassage,
  PublicReadingPassage,
  ReadingPassageWithDetails,
  CreateReadingPassageRequest,
  UpdateReadingPassageRequest,
  ClassicalPassage,
  PublicClassicalPassage,
  ClassicalPassageWithDetails,
  CreateClassicalPassageRequest,
  UpdateClassicalPassageRequest,
} from '../types';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    // 打印环境配置信息（仅开发环境）
    logEnvironmentInfo();

    this.client = axios.create({
      baseURL: appConfig.apiBaseUrl,
      timeout: 30000,
      withCredentials: true, // 包含cookies
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // 请求拦截器
    this.client.interceptors.request.use(
      (config) => {
        console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        console.error('[API] Request error:', error);
        return Promise.reject(error);
      }
    );

    // 响应拦截器
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        console.log(`[API] Response ${response.status}:`, response.data);
        return response;
      },
      (error) => {
        console.error('[API] Response error:', error.response?.data || error.message);
        
        // 处理401错误 - 未授权
        if (error.response?.status === 401) {
          // 只有在非登录/注册页面时才重定向到登录页
          const currentPath = window.location.pathname;
          if (currentPath !== '/login' && currentPath !== '/register') {
            window.location.href = '/login';
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  // 认证相关API
  async register(data: RegisterRequest): Promise<AuthResponse> {
    const response = await this.client.post<AuthResponse>('/auth/register', data);
    return response.data;
  }

  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await this.client.post<AuthResponse>('/auth/login', data);
    return response.data;
  }

  async logout(): Promise<ApiResponse> {
    const response = await this.client.post<ApiResponse>('/auth/logout');
    return response.data;
  }

  async getCurrentUser(): Promise<{ user: User }> {
    const response = await this.client.get<{ user: User }>('/auth/me');
    return response.data;
  }

  // 学习集相关API
  async getStudySets(params?: { tags?: string[] }): Promise<{ sets: StudySet[] }> {
    const qs = new URLSearchParams();
    if (params?.tags && params.tags.length > 0) qs.append('tags', params.tags.join(','));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const response = await this.client.get<{ sets: StudySet[] }>(`/sets${suffix}`);
    return response.data;
  }

  async getPublicStudySets(params?: { tags?: string[] }): Promise<{ sets: PublicStudySet[] }> {
    const qs = new URLSearchParams();
    if (params?.tags && params.tags.length > 0) qs.append('tags', params.tags.join(','));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const response = await this.client.get<{ sets: PublicStudySet[] }>(`/sets/public${suffix}`);
    return response.data;
  }

  async createStudySet(data: CreateStudySetRequest): Promise<ApiResponse> {
    const response = await this.client.post<ApiResponse>('/sets', data);
    return response.data;
  }

  // 标签相关API
  async getTags(type?: 'grade' | 'subject' | 'custom'): Promise<{ tags: Tag[] }> {
    const qs = new URLSearchParams();
    if (type) qs.append('type', type);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const response = await this.client.get<{ tags: Tag[] }>(`/tags${suffix}`);
    return response.data;
  }

  async createCustomTag(name: string): Promise<{ tag: Tag }> {
    const response = await this.client.post<{ tag: Tag }>(`/tags`, { name, type: 'custom' });
    return response.data;
  }

  async applyTagsToSet(setId: string, tagIds: string[]): Promise<ApiResponse> {
    const response = await this.client.post<ApiResponse>(`/tags/apply`, { set_id: setId, tag_ids: tagIds });
    return response.data;
  }

  // 卡片标签相关API
  async applyTagsToCard(cardId: string, tagIds: string[]): Promise<ApiResponse> {
    const response = await this.client.post<ApiResponse>(`/tags/apply-to-card`, { card_id: cardId, tag_ids: tagIds });
    return response.data;
  }

  async getCardTags(cardId: string): Promise<{ tags: Tag[] }> {
    const response = await this.client.get<{ tags: Tag[] }>(`/tags/card/${cardId}`);
    return response.data;
  }

  // 文言文材料标签相关API
  async applyTagsToClassicalPassage(passageId: string, tagIds: string[]): Promise<ApiResponse> {
    const response = await this.client.post<ApiResponse>(`/tags/apply-to-classical-passage`, { passage_id: passageId, tag_ids: tagIds });
    return response.data;
  }

  async getClassicalPassageTags(passageId: string): Promise<{ tags: Tag[] }> {
    const response = await this.client.get<{ tags: Tag[] }>(`/tags/classical-passage/${passageId}`);
    return response.data;
  }

  async getStudySet(id: string): Promise<StudySetDetails> {
    const response = await this.client.get<StudySetDetails>(`/sets/${id}`);
    return response.data;
  }

  async updateStudySet(id: string, data: UpdateStudySetRequest): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/sets/${id}`, data);
    return response.data;
  }

  // 更新学习集基本信息（不包括卡片）
  async updateStudySetInfo(id: string, data: { title?: string; description?: string; is_public?: boolean }): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/sets/${id}`, data);
    return response.data;
  }

  async deleteStudySet(id: string): Promise<ApiResponse> {
    const response = await this.client.delete<ApiResponse>(`/sets/${id}`);
    return response.data;
  }

  // ==================== 卡片 CRUD 方法 ====================
  
  // 获取学习集的所有卡片
  async getCards(setId: string): Promise<{ cards: any[] }> {
    const response = await this.client.get<{ cards: any[] }>(`/sets/${setId}/cards`);
    return response.data;
  }

  // 创建单个卡片
  async createCard(setId: string, data: CreateCardRequest): Promise<ApiResponse & { card?: any }> {
    const response = await this.client.post<ApiResponse & { card?: any }>(`/sets/${setId}/cards`, data);
    return response.data;
  }

  // 更新单个卡片
  async updateCard(setId: string, cardId: string, data: UpdateCardRequest): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/sets/${setId}/cards/${cardId}`, data);
    return response.data;
  }

  // 删除单个卡片
  async deleteCard(setId: string, cardId: string): Promise<ApiResponse> {
    const response = await this.client.delete<ApiResponse>(`/sets/${setId}/cards/${cardId}`);
    return response.data;
  }

  // ==================== 卡片批量操作方法 ====================
  
  // 批量创建卡片
  async createCardsBatch(setId: string, data: CreateCardsBatchRequest): Promise<ApiResponse & { created_count?: number }> {
    const response = await this.client.post<ApiResponse & { created_count?: number }>(`/sets/${setId}/cards/batch`, data);
    return response.data;
  }

  // 批量更新卡片
  async updateCardsBatch(setId: string, data: {
    cards: Array<{
      id: string;
      term?: string;
      definition?: string;
      term_image_url?: string;
      definition_image_url?: string;
      sort_order?: number;
    }>;
  }): Promise<ApiResponse & { updated_count?: number }> {
    const response = await this.client.put<ApiResponse & { updated_count?: number }>(`/sets/${setId}/cards/batch`, data);
    return response.data;
  }

  // 批量删除卡片
  async deleteCardsBatch(setId: string, data: {
    card_ids: string[];
  }): Promise<ApiResponse & { deleted_count?: number }> {
    const response = await this.client.delete<ApiResponse & { deleted_count?: number }>(`/sets/${setId}/cards/batch`, { data });
    return response.data;
  }

  // 健康检查
  async healthCheck(): Promise<ApiResponse> {
    const response = await this.client.get<ApiResponse>('/health');
    return response.data;
  }

  // 用户设置相关API
  async getUserSettings(): Promise<{ settings: UserSettings }> {
    const response = await this.client.get<{ settings: UserSettings }>('/user/settings');
    return response.data;
  }

  async updateUserSettings(settings: Partial<UserSettings>): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>('/user/settings', settings);
    return response.data;
  }

  // 学习进度相关API
  async getStudySession(setId: string): Promise<{ session: StudySessionData }> {
    const response = await this.client.get<{ session: StudySessionData }>(`/sessions/${setId}`);
    return response.data;
  }

  async saveStudySession(setId: string, data: SaveStudySessionRequest): Promise<ApiResponse> {
    const response = await this.client.post<ApiResponse>(`/sessions/${setId}`, data);
    return response.data;
  }

  async resetStudySession(setId: string): Promise<ApiResponse> {
    const response = await this.client.delete<ApiResponse>(`/sessions/${setId}`);
    return response.data;
  }

  // 图片上传相关API
  async uploadImage(file: File): Promise<{ image_url: string; file_name: string; message: string }> {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await this.client.post<{ image_url: string; file_name: string; message: string }>('/images/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // 自适应学习（微型SRS）- 闪卡模式
  async startAdaptiveFlashcardSession(setId: string): Promise<AdaptiveStartResponseFlashcard> {
    const response = await this.client.post<AdaptiveStartResponseFlashcard>(`/sets/${setId}/session/start`, {
      mode: 'flashcard' satisfies StudyMode
    });
    return response.data as unknown as AdaptiveStartResponseFlashcard;
  }

  async answerAdaptiveFlashcard(setId: string, params: { session: AdaptiveSessionState; cardId: string; knewIt: boolean; }): Promise<AdaptiveAnswerResponseFlashcard> {
    const response = await this.client.post<AdaptiveAnswerResponseFlashcard>(`/sets/${setId}/session/answer`, {
      mode: 'flashcard' satisfies StudyMode,
      session: params.session,
      cardId: params.cardId,
      knewIt: params.knewIt
    });
    return response.data as unknown as AdaptiveAnswerResponseFlashcard;
  }

  // 学习时间统计相关API
  async startStudyTime(data: StartStudyTimeRequest): Promise<{ log_id: string; message: string }> {
    const response = await this.client.post<{ log_id: string; message: string }>('/study-time/start', data);
    return response.data;
  }

  async updateStudyTime(data: UpdateStudyTimeRequest): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>('/study-time/update', data);
    return response.data;
  }

  async endStudyTime(data: EndStudyTimeRequest): Promise<ApiResponse> {
    const response = await this.client.post<ApiResponse>('/study-time/end', data);
    return response.data;
  }

  async getStudyTimeStats(period?: string, setId?: string): Promise<StudyTimeStats> {
    const params = new URLSearchParams();
    if (period) params.append('period', period);
    if (setId) params.append('set_id', setId);
    const response = await this.client.get<StudyTimeStats>(`/study-time/stats?${params.toString()}`);
    return response.data;
  }

  async getRecentStudyTimeLogs(limit?: number): Promise<{ logs: StudyTimeLog[] }> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    const response = await this.client.get<{ logs: StudyTimeLog[] }>(`/study-time/recent?${params.toString()}`);
    return response.data;
  }

  // ==================== 阅读材料相关API ====================

  // 获取当前用户的所有阅读材料
  async getReadingPassages(): Promise<{ passages: ReadingPassage[] }> {
    const response = await this.client.get<{ passages: ReadingPassage[] }>('/passages');
    return response.data;
  }

  // 获取所有公开的阅读材料
  async getPublicReadingPassages(): Promise<{ passages: PublicReadingPassage[] }> {
    const response = await this.client.get<{ passages: PublicReadingPassage[] }>('/passages/public');
    return response.data;
  }

  // 创建新的阅读材料
  async createReadingPassage(data: CreateReadingPassageRequest): Promise<ApiResponse & { passage?: ReadingPassage }> {
    const response = await this.client.post<ApiResponse & { passage?: ReadingPassage }>('/passages', data);
    return response.data;
  }

  // 获取指定阅读材料详情
  async getReadingPassage(id: string): Promise<{ passage: ReadingPassageWithDetails; author?: any; is_owner: boolean }> {
    const response = await this.client.get<{ passage: ReadingPassageWithDetails; author?: any; is_owner: boolean }>(`/passages/${id}`);
    return response.data;
  }

  // 更新阅读材料
  async updateReadingPassage(id: string, data: UpdateReadingPassageRequest): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/passages/${id}`, data);
    return response.data;
  }

  // 删除阅读材料
  async deleteReadingPassage(id: string): Promise<ApiResponse> {
    const response = await this.client.delete<ApiResponse>(`/passages/${id}`);
    return response.data;
  }

  // 触发LLM提取阅读材料内容（异步）
  async triggerPassageExtraction(passageId: string): Promise<{ message: string; processing_status: string }> {
    const response = await this.client.post<{ message: string; processing_status: string }>(`/passages/${passageId}/extract`);
    return response.data;
  }

  // 更新阅读材料的词汇列表
  async updatePassageVocabularies(id: string, data: { vocabularies: Array<{ id?: string; word: string; explanation: string; example_sentence?: string }> }): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/passages/${id}/vocabularies`, data);
    return response.data;
  }

  // 更新阅读材料的语法考点
  async updatePassageGrammar(id: string, data: { grammar_points: Array<{ id?: string; point_name: string; explanation: string; example_text?: string }> }): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/passages/${id}/grammar`, data);
    return response.data;
  }

  // 更新阅读材料的固定搭配
  async updatePassageCollocations(id: string, data: { collocations: Array<{ id?: string; collocation: string; meaning: string; usage_note?: string }> }): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/passages/${id}/collocations`, data);
    return response.data;
  }

  // 更新阅读材料的复杂句子分析
  async updatePassageComplexSentences(id: string, data: { complex_sentences: Array<{ id?: string; sentence: string; structure_analysis: string; translation: string }> }): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/passages/${id}/complex-sentences`, data);
    return response.data;
  }

  // 提取选中的文本内容
  async extractSelectedText(passageId: string, selectedText: string): Promise<ApiResponse> {
    const response = await this.client.post<ApiResponse>(
      `/passages/${passageId}/extract-selection`,
      { selected_text: selectedText }
    );
    return response.data;
  }

  // ==================== 文言文学习相关API ====================

  // 获取当前用户的所有文言文材料
  async getClassicalPassages(): Promise<{ passages: ClassicalPassage[] }> {
    const response = await this.client.get<{ passages: ClassicalPassage[] }>('/classical-chinese');
    return response.data;
  }

  // 获取所有公开的文言文材料
  async getPublicClassicalPassages(): Promise<{ passages: PublicClassicalPassage[] }> {
    const response = await this.client.get<{ passages: PublicClassicalPassage[] }>('/classical-chinese/public');
    return response.data;
  }

  // 创建新的文言文材料
  async createClassicalPassage(data: CreateClassicalPassageRequest): Promise<ApiResponse & { passage?: ClassicalPassage }> {
    const response = await this.client.post<ApiResponse & { passage?: ClassicalPassage }>('/classical-chinese', data);
    return response.data;
  }

  // 获取指定文言文材料详情
  async getClassicalPassage(id: string): Promise<{ passage: ClassicalPassageWithDetails; author?: any; is_owner: boolean }> {
    const response = await this.client.get<{ passage: ClassicalPassageWithDetails; author?: any; is_owner: boolean }>(`/classical-chinese/${id}`);
    return response.data;
  }

  // 更新文言文材料
  async updateClassicalPassage(id: string, data: UpdateClassicalPassageRequest): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/classical-chinese/${id}`, data);
    return response.data;
  }

  // 删除文言文材料
  async deleteClassicalPassage(id: string): Promise<ApiResponse> {
    const response = await this.client.delete<ApiResponse>(`/classical-chinese/${id}`);
    return response.data;
  }

  // 触发LLM提取文言文材料内容（异步）
  async triggerClassicalExtraction(passageId: string): Promise<{ message: string; processing_status: string }> {
    const response = await this.client.post<{ message: string; processing_status: string }>(`/classical-chinese/${passageId}/extract`);
    return response.data;
  }

  // ==================== 文言文提取内容更新API ====================

  // 更新译文
  async updateClassicalTranslations(passageId: string, data: { translations: Array<{ id?: string; translation: string; paragraph_index?: number }> }): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/classical-chinese/${passageId}/translations`, data);
    return response.data;
  }

  // 更新主旨思想
  async updateClassicalMainIdeas(passageId: string, data: { main_ideas: Array<{ id?: string; main_idea: string; emotion?: string; paragraph_index?: number }> }): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/classical-chinese/${passageId}/main-ideas`, data);
    return response.data;
  }

  // 更新重点实词
  async updateClassicalKeyWords(passageId: string, data: { key_words: Array<{ id?: string; word: string; meaning_in_text: string; multiple_meanings?: string; ancient_modern_diff?: string; example_sentence?: string }> }): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/classical-chinese/${passageId}/key-words`, data);
    return response.data;
  }

  // 更新核心虚词
  async updateClassicalFunctionWords(passageId: string, data: { function_words: Array<{ id?: string; word: string; word_class: string; function_description: string; example_sentence?: string }> }): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/classical-chinese/${passageId}/function-words`, data);
    return response.data;
  }

  // 更新通假字与古今异义
  async updateClassicalSpecialChars(passageId: string, data: { special_chars: Array<{ id?: string; char_type: 'tongjiazi' | 'gujinyiyi'; original_char: string; correct_char?: string; pronunciation?: string; ancient_meaning?: string; modern_meaning?: string; example_sentence?: string }> }): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/classical-chinese/${passageId}/special-chars`, data);
    return response.data;
  }

  // 更新特殊句式
  async updateClassicalSentencePatterns(passageId: string, data: { sentence_patterns: Array<{ id?: string; pattern_type: string; pattern_name: string; explanation: string; example_sentence: string; normal_order?: string }> }): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/classical-chinese/${passageId}/sentence-patterns`, data);
    return response.data;
  }

  // 更新词类活用
  async updateClassicalWordUsage(passageId: string, data: { word_usage: Array<{ id?: string; usage_type: string; word: string; explanation: string; example_sentence: string }> }): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/classical-chinese/${passageId}/word-usage`, data);
    return response.data;
  }

  // 更新修辞手法
  async updateClassicalRhetoric(passageId: string, data: { rhetoric: Array<{ id?: string; rhetoric_type: string; explanation: string; example_sentence: string; effect_description?: string }> }): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/classical-chinese/${passageId}/rhetoric`, data);
    return response.data;
  }

  // 更新文学常识
  async updateClassicalLiteraryKnowledge(passageId: string, data: { literary_knowledge: Array<{ id?: string; knowledge_type: 'source' | 'author' | 'allusion' | 'background'; title: string; content: string; paragraph_index?: number }> }): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/classical-chinese/${passageId}/literary-knowledge`, data);
    return response.data;
  }

  // 更新人物形象分析
  async updateClassicalCharacterAnalysis(passageId: string, data: { character_analysis: Array<{ id?: string; character_name: string; personality_traits: string; analysis: string; supporting_evidence?: string; paragraph_index?: number }> }): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/classical-chinese/${passageId}/character-analysis`, data);
    return response.data;
  }

  // 更新写作特色
  async updateClassicalWritingFeatures(passageId: string, data: { writing_features: Array<{ id?: string; feature_type: string; title: string; description: string; paragraph_index?: number }> }): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/classical-chinese/${passageId}/writing-features`, data);
    return response.data;
  }

  // 更新启示与思考
  async updateClassicalInsights(passageId: string, data: { insights: Array<{ id?: string; insight: string; modern_significance?: string; paragraph_index?: number }> }): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/classical-chinese/${passageId}/insights`, data);
    return response.data;
  }

  // ==================== 文言文背诵功能相关API ====================

  // 开始背诵会话
  async startClassicalRecitation(passageId: string): Promise<ApiResponse> {
    const response = await this.client.post<ApiResponse>(`/classical-chinese/${passageId}/recitation/start`);
    return response.data;
  }

  // 更新背诵进度
  async updateRecitationProgress(passageId: string, sessionId: string, data: {
    current_level: number;
    mastery_levels: number[];
    accuracy: number;
    completed_levels: number[];
  }): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/classical-chinese/${passageId}/recitation/${sessionId}`, data);
    return response.data;
  }

  // 获取背诵历史
  async getRecitationHistory(passageId: string): Promise<ApiResponse> {
    const response = await this.client.get<ApiResponse>(`/classical-chinese/${passageId}/recitation/history`);
    return response.data;
  }

  // 获取背诵会话状态
  async getRecitationStatus(passageId: string): Promise<ApiResponse> {
    const response = await this.client.get<ApiResponse>(`/classical-chinese/${passageId}/recitation/status`);
    return response.data;
  }
}

// 创建并导出单例实例
export const apiClient = new ApiClient();
export default apiClient;
